(() => {
  'use strict';
  if (window.__OAT_DISABLE_CARD_SWATCH_SWITCH__) return;

  // -----------------------------
  // Helpers
  // -----------------------------
  function getSwatchInputFromNode(node) {
    if (!(node instanceof Element)) return null;

    if (node.matches('input[type="radio"][data-option-media-id]')) return node;

    const label = node.closest('label');
    if (!label) return null;

    const input = label.querySelector('input[type="radio"][data-option-media-id]');
    return input || null;
  }

  function isSwatchInput(input) {
    return (
      input &&
      input instanceof HTMLInputElement &&
      input.type === 'radio' &&
      !!input.dataset.optionMediaId &&
      !!input.closest('swatches-variant-picker-component')
    );
  }

  function getProductCardFromNode(node) {
    return node instanceof Element ? node.closest('product-card') : null;
  }

  function getSlideshowFromProductCard(pc) {
    if (!pc) return null;
    const gallery = pc.querySelector('[ref="cardGallery"]') || pc.querySelector('.card-gallery');
    if (!gallery) return null;
    return gallery.querySelector('slideshow-component');
  }

  function ensureCardVisibleSlide(pc) {
    if (!pc) return;

    const slideshow = getSlideshowFromProductCard(pc);
    if (!slideshow) return;

    hardEnableSlideshow(slideshow);

    const visibleSlide = slideshow.querySelector('slideshow-slide[aria-hidden="false"]:not([hidden])');
    if (visibleSlide) return;

    const checked = pc.querySelector('input[type="radio"][data-option-media-id]:checked');
    const preferredId = checked?.dataset?.optionMediaId;
    const firstSlide = slideshow.querySelector('slideshow-slide[slide-id]');
    const fallbackId = firstSlide?.getAttribute('slide-id');
    const targetId = String(preferredId || fallbackId || '');
    if (!targetId) return;

    try {
      if (typeof slideshow.select === 'function') {
        slideshow.select({ id: targetId }, undefined, { animate: false });
      }
    } catch (_) {}

    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide[slide-id]'));
    slides.forEach((slide) => {
      const on = slide.getAttribute('slide-id') === targetId;
      slide.hidden = !on;
      slide.toggleAttribute('hidden', !on);
      slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      slide.style.display = on ? '' : 'none';
    });

    const activeImage = slideshow.querySelector(`slideshow-slide[slide-id="${targetId}"] img`);
    if (activeImage) {
      try { activeImage.loading = 'eager'; } catch (_) {}
      try { activeImage.fetchPriority = 'high'; } catch (_) {}
    }
  }

  function hardEnableSlideshow(slideshow) {
    if (!slideshow) return;
    // boolean attribute: disabled="false" still disables
    slideshow.removeAttribute('disabled');
    queueMicrotask(() => slideshow.removeAttribute('disabled'));
    requestAnimationFrame(() => slideshow.removeAttribute('disabled'));
    try { slideshow.disabled = false; } catch (_) {}
  }

  function selectSlide(slideshow, mediaId) {
    if (!slideshow || !mediaId) return;
    const id = String(mediaId);
    hardEnableSlideshow(slideshow);

    try {
      if (typeof slideshow.select === 'function') {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    } catch (_) {}

    // Fallback: dispatch the same event Horizon listens to (if select is missing)
    slideshow.dispatchEvent(
      new CustomEvent('slideshow:select', { bubbles: true, detail: { id, animate: false } })
    );
  }


  // Keep highlight state consistent WITHOUT fighting Horizon’s dataset system.
  // Only touch classes on labels in this picker, and trust the browser checked state.
  function syncLabelHighlightWithinPicker(input) {
    const picker = input.closest('swatches-variant-picker-component');
    if (!picker) return;

    const radios = Array.from(
      picker.querySelectorAll('input[type="radio"][data-option-media-id]')
    );

    radios.forEach((r) => {
      const lbl = r.closest('label');
      if (!lbl) return;
      const chosen = r.checked;
      lbl.classList.toggle('is-selected', chosen);
      if (chosen) lbl.setAttribute('data-selected', '');
      else lbl.removeAttribute('data-selected');
    });
  }

  // Swatch navigation blocking is now owned by product-card itself via
  // `[data-oat-stop-card-nav]` and local card click guards.
  // Let native radio/label behavior run so the browser actually moves `checked`
  // and Horizon can update `data-current-checked` / `data-previous-checked`.


  
  // -----------------------------
  //  B) On change: switch media + pin links
  // -----------------------------
  document.addEventListener(
    'change',
    (e) => {
      const input = getSwatchInputFromNode(e.target);
      if (!isSwatchInput(input)) return;

      // Only swatch radios (your naming convention)
      if (!input.name || !input.name.includes('-swatch')) return;

      const pc = getProductCardFromNode(input);
      if (!pc) return;

      const mediaId = input.dataset.optionMediaId;
      const variantId = input.dataset.variantId;
      if (!mediaId || !variantId) return;

      // Store intent so we can re-apply after morph
      pc.dataset.oatDesiredMediaId = String(mediaId);
      pc.dataset.oatDesiredVariantId = String(variantId);

      // Sync highlight in this picker only
      syncLabelHighlightWithinPicker(input);

      // Switch image now
      const slideshow = getSlideshowFromProductCard(pc);
      if (slideshow) selectSlide(slideshow, mediaId);
      ensureCardVisibleSlide(pc);

      input.blur();
    },
    true
  );

  // -----------------------------
  //  C) After Horizon morph (variant:update), re-apply the desired media
  // -----------------------------
  document.addEventListener(
    'variant:update',
    (e) => {
      if (!(e.target instanceof Element)) return;
      const pc = e.target.closest('product-card');
      if (!pc) return;

      const mediaId = pc.dataset.oatDesiredMediaId;
      const variantId = pc.dataset.oatDesiredVariantId;
      if (!mediaId || !variantId) return;

      // Let custom elements + morph settle
      queueMicrotask(() => {
        requestAnimationFrame(() => {
          const slideshow = getSlideshowFromProductCard(pc);
          if (slideshow) selectSlide(slideshow, mediaId);
          ensureCardVisibleSlide(pc);

          // Re-sync highlight based on the currently checked radio in the new DOM
          const checked = pc.querySelector(
            'swatches-variant-picker-component input[type="radio"][data-option-media-id]:checked'
          );
          if (checked) syncLabelHighlightWithinPicker(checked);
        });
      });
    },
    true
  );
})();
